import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Loader2, AlertTriangle } from 'lucide-react';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

// Serve the worker locally from /public (no external CDN dependency).
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

const escapeHtml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Renders a PDF (from a Blob/ArrayBuffer) with a text layer. Each `highlight`
 *  entry is matched as a CONTIGUOUS phrase (whitespace-flexible) and only its
 *  FIRST occurrence is highlighted — so we highlight the exact evidence quote and
 *  the exact clause/section reference, not every word scattered across the doc.
 *  `highlight[0]` is treated as the primary (evidence quote) and is scrolled to. */
export const PdfDocViewer = ({ data, highlight = [] }) => {
  const [numPages, setNumPages] = useState(0);
  const [error, setError] = useState(false);
  const [width, setWidth] = useState(760);
  const [tick, setTick] = useState(0);
  const containerRef = useRef(null);
  const scrolledRef = useRef(false);
  const rangesRef = useRef({});      // `${page}:${itemIndex}` -> [[start,end,isPrimary], ...]
  const computedRef = useRef(new Set());

  useLayoutEffect(() => {
    const measure = () => {
      if (containerRef.current) setWidth(Math.min(Math.max(containerRef.current.clientWidth - 24, 320), 1000));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // The phrases to locate: the evidence quote (primary) + the clause/section ref.
  const phrases = useMemo(
    () => (highlight || []).map((p) => (p || '').replace(/\s+/g, ' ').trim()).filter((p) => p.length >= 2),
    [highlight],
  );

  // Reset caches whenever the document or the phrases change.
  useEffect(() => {
    rangesRef.current = {};
    computedRef.current = new Set();
    scrolledRef.current = false;
    setTick((t) => t + 1);
  }, [data, phrases]);

  // Locate each phrase inside a page's concatenated text (phrase spans multiple
  // text items), then map the match back to per-item character ranges.
  const computePage = useCallback((page, textContent) => {
    if (computedRef.current.has(page)) return;
    computedRef.current.add(page);
    const items = (textContent && textContent.items) || [];
    let concat = '';
    const spans = [];
    items.forEach((it, idx) => {
      const s = it.str || '';
      const start = concat.length;
      concat += s;
      spans.push({ idx, start, end: concat.length });
      concat += ' '; // join spaces between items so cross-item phrases match
    });

    const globalRanges = []; // [start, end, isPrimary]
    phrases.forEach((phrase, pIdx) => {
      const isPrimary = pIdx === 0;
      // Try the full phrase first, then shorter contiguous prefixes for long quotes.
      const words = phrase.split(' ');
      const candidates = [phrase];
      if (words.length > 12) candidates.push(words.slice(0, 12).join(' '));
      if (words.length > 8) candidates.push(words.slice(0, 8).join(' '));
      if (words.length > 5) candidates.push(words.slice(0, 5).join(' '));
      for (const cand of candidates) {
        if (cand.length < 2) continue;
        let re;
        try { re = new RegExp(escapeRe(cand).replace(/\s+/g, '\\s+'), 'i'); }
        catch { continue; }
        const m = re.exec(concat);
        if (m && m[0]) { globalRanges.push([m.index, m.index + m[0].length, isPrimary]); break; }
      }
    });

    globalRanges.forEach(([gs, ge, primary]) => {
      spans.forEach((sp) => {
        const s = Math.max(gs, sp.start);
        const e = Math.min(ge, sp.end);
        if (e > s) {
          const key = `${page}:${sp.idx}`;
          (rangesRef.current[key] = rangesRef.current[key] || []).push([s - sp.start, e - sp.start, primary]);
        }
      });
    });
    if (globalRanges.length) setTick((t) => t + 1);
  }, [phrases]);

  const renderItem = useCallback((page) => (item) => {
    const str = item.str || '';
    const ranges = rangesRef.current[`${page}:${item.itemIndex}`];
    if (!ranges || !ranges.length || !str) return escapeHtml(str);
    const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
    let out = '';
    let cursor = 0;
    sorted.forEach(([s0, e0, primary]) => {
      let s = s0 < cursor ? cursor : s0;
      const e = e0;
      if (e <= s) return;
      out += escapeHtml(str.slice(cursor, s));
      out += `<mark class="rp-hl${primary ? ' rp-hl-primary' : ''}">` + escapeHtml(str.slice(s, e)) + '</mark>';
      cursor = e;
    });
    out += escapeHtml(str.slice(cursor));
    return out;
  }, [tick]);

  const onTextLayerRendered = useCallback(() => {
    if (scrolledRef.current || !containerRef.current) return;
    const el = containerRef.current.querySelector('.rp-hl-primary') || containerRef.current.querySelector('.rp-hl');
    if (el) { scrolledRef.current = true; el.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
  }, [tick]);

  const file = useMemo(() => (data ? { data } : null), [data]);

  return (
    <div ref={containerRef} className="h-full w-full overflow-y-auto bg-slate-100 flex flex-col items-center py-4 gap-4" data-testid="pdf-doc-viewer">
      {error && (
        <div className="h-full flex flex-col items-center justify-center text-slate-500" data-testid="doc-viewer-error">
          <AlertTriangle className="h-6 w-6 text-amber-500" /><span className="text-sm mt-2">Couldn’t render this document.</span>
        </div>
      )}
      {file && !error && (
        <Document
          file={file}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          onLoadError={() => setError(true)}
          loading={<div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /><span className="text-sm ml-2">Loading document…</span></div>}
        >
          {Array.from({ length: numPages }, (_, i) => {
            const page = i + 1;
            return (
              <div key={i} className="shadow-sm mb-1 bg-white" data-testid={`pdf-page-${page}`}>
                <Page
                  pageNumber={page}
                  width={width}
                  renderAnnotationLayer={false}
                  customTextRenderer={renderItem(page)}
                  onGetTextSuccess={(tc) => computePage(page, tc)}
                  onRenderTextLayerSuccess={onTextLayerRendered}
                  loading={<div style={{ height: width * 1.3 }} className="flex items-center justify-center text-slate-300"><Loader2 className="h-5 w-5 animate-spin" /></div>}
                />
              </div>
            );
          })}
        </Document>
      )}
    </div>
  );
};
